import React, { forwardRef } from "react";

/**
 * Card — Ocean Blue Design System
 *
 * Subcomponents:
 *   Card           — base wrapper
 *   Card.Header    — top section with title + optional actions slot
 *   Card.Body      — content padding wrapper
 *   Card.Footer    — bottom section with action buttons slot
 *   Card.Section   — horizontal divider-separated inner section
 *
 * Props:
 *  variant  : "default" | "interactive" | "flat" | "outlined"
 *  noPadding: boolean — removes default padding from Card.Body
 */

const CARD_VARIANTS = {
  default:
    "bg-white border border-[#E2E8F0] " +
    "shadow-[0_1px_3px_rgba(15,23,42,0.06),0_1px_2px_rgba(15,23,42,0.04)] " +
    "rounded-xl",
  interactive:
    "bg-white border border-[#E2E8F0] " +
    "shadow-[0_1px_3px_rgba(15,23,42,0.06),0_1px_2px_rgba(15,23,42,0.04)] " +
    "rounded-xl transition-all duration-200 ease-out cursor-pointer " +
    "hover:-translate-y-1 hover:border-[#CBD5E1] " +
    "hover:shadow-[0_6px_20px_rgba(15,23,42,0.10),0_2px_8px_rgba(15,23,42,0.06)]",
  flat: "bg-white rounded-xl",
  outlined:
    "bg-transparent border border-[#E2E8F0] rounded-xl",
};

/* ── Root Card ─────────────────────────────────────── */
const Card = forwardRef(
  (
    {
      children,
      variant = "default",
      className = "",
      as: Tag = "div",
      ...props
    },
    ref
  ) => (
    <Tag
      ref={ref}
      className={[CARD_VARIANTS[variant] ?? CARD_VARIANTS.default, className]
        .filter(Boolean)
        .join(" ")}
      {...props}
    >
      {children}
    </Tag>
  )
);
Card.displayName = "Card";

/* ── Card.Header ───────────────────────────────────── */
const CardHeader = ({ title, subtitle, actions, className = "", children }) => (
  <div
    className={[
      "flex items-center justify-between px-6 py-4",
      "border-b border-[#F1F5F9]",
      className,
    ]
      .filter(Boolean)
      .join(" ")}
  >
    <div className="min-w-0">
      {title && (
        <h3 className="text-sm font-semibold text-[#1E293B] leading-tight truncate">
          {title}
        </h3>
      )}
      {subtitle && (
        <p className="text-xs text-[#94A3B8] mt-0.5 truncate">{subtitle}</p>
      )}
      {children}
    </div>
    {actions && (
      <div className="flex items-center gap-2 ml-4 flex-shrink-0">{actions}</div>
    )}
  </div>
);
CardHeader.displayName = "Card.Header";

/* ── Card.Body ─────────────────────────────────────── */
const CardBody = ({ children, className = "", noPadding = false }) => (
  <div
    className={[noPadding ? "" : "p-6", className].filter(Boolean).join(" ")}
  >
    {children}
  </div>
);
CardBody.displayName = "Card.Body";

/* ── Card.Footer ───────────────────────────────────── */
const CardFooter = ({ children, className = "", align = "right" }) => (
  <div
    className={[
      "flex items-center gap-3 px-6 py-4 border-t border-[#F1F5F9]",
      align === "right"
        ? "justify-end"
        : align === "center"
          ? "justify-center"
          : "justify-start",
      className,
    ]
      .filter(Boolean)
      .join(" ")}
  >
    {children}
  </div>
);
CardFooter.displayName = "Card.Footer";

/* ── Card.Section ──────────────────────────────────── */
const CardSection = ({ children, className = "", title }) => (
  <div className={["px-6 py-4 border-b border-[#F1F5F9] last:border-b-0", className].join(" ")}>
    {title && (
      <p className="text-xs font-semibold text-[#94A3B8] uppercase tracking-widest mb-3">
        {title}
      </p>
    )}
    {children}
  </div>
);
CardSection.displayName = "Card.Section";

/* ── Attach subcomponents ──────────────────────────── */
Card.Header = CardHeader;
Card.Body = CardBody;
Card.Footer = CardFooter;
Card.Section = CardSection;

export default Card;
